import React, { useMemo } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

const WINDOW_SIZE = 5;

/**
 * Sliding window of up to WINDOW_SIZE page numbers.
 * Prefers starting at currentPage, but shifts backward near the end so the
 * window stays full when possible (e.g. page 7 of 7 → 3 4 5 6 7).
 */
export function getVisiblePages(currentPage, totalPages, windowSize = WINDOW_SIZE) {
  if (totalPages < 1) return [];
  const maxStart = Math.max(1, totalPages - windowSize + 1);
  const start = Math.min(Math.max(1, currentPage), maxStart);
  const end = Math.min(totalPages, start + windowSize - 1);
  const pages = [];
  for (let p = start; p <= end; p += 1) pages.push(p);
  return pages;
}

/**
 * Sliding page-number pagination: << < 1 2 3 4 5 > >>
 * Window starts at the current page and shows up to 5 pages.
 */
function ListPagination({
  currentPage,
  totalPages,
  onPageChange,
  disabled = false,
  className = '',
}) {
  const pages = useMemo(
    () => getVisiblePages(currentPage, totalPages),
    [currentPage, totalPages]
  );

  if (totalPages <= 1) return null;

  const canPrev = currentPage > 1;
  const canNext = currentPage < totalPages;

  const goTo = (page) => {
    if (disabled || page < 1 || page > totalPages || page === currentPage) return;
    onPageChange(page);
  };

  const navBtnClass =
    'p-1.5 rounded border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-transparent';

  return (
    <div className={`flex items-center gap-1 ${className}`.trim()} role="navigation" aria-label="Pagination">
      <button
        type="button"
        onClick={() => goTo(1)}
        disabled={disabled || !canPrev}
        className={navBtnClass}
        aria-label="First page"
        title="First"
      >
        <ChevronsLeft className="h-5 w-5" />
      </button>
      <button
        type="button"
        onClick={() => goTo(currentPage - 1)}
        disabled={disabled || !canPrev}
        className={navBtnClass}
        aria-label="Previous page"
        title="Previous"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>

      {pages.map((page) => {
        const isCurrent = page === currentPage;
        return (
          <button
            key={page}
            type="button"
            onClick={() => goTo(page)}
            disabled={disabled}
            className={
              isCurrent
                ? 'min-w-[1.75rem] px-1.5 py-1 rounded font-bold text-gray-900 tabular-nums'
                : 'min-w-[1.75rem] px-1.5 py-1 rounded text-gray-700 tabular-nums hover:bg-gray-50 hover:underline disabled:opacity-40'
            }
            aria-label={`Page ${page}`}
            aria-current={isCurrent ? 'page' : undefined}
          >
            {page}
          </button>
        );
      })}

      <button
        type="button"
        onClick={() => goTo(currentPage + 1)}
        disabled={disabled || !canNext}
        className={navBtnClass}
        aria-label="Next page"
        title="Next"
      >
        <ChevronRight className="h-5 w-5" />
      </button>
      <button
        type="button"
        onClick={() => goTo(totalPages)}
        disabled={disabled || !canNext}
        className={navBtnClass}
        aria-label="Last page"
        title="Last"
      >
        <ChevronsRight className="h-5 w-5" />
      </button>
    </div>
  );
}

export default ListPagination;
